import { z } from 'zod';
import { StaffLevel } from './admin-users.js';
/**
 * Neo của thang story point — APM-SPA, docs/plan-estimate-by-reference.md §6.
 *
 * "1 SP ≈ công một người bậc `level` làm trong `hours` giờ", theo tổ chức. Người
 * dùng chốt 2026-10-22, mặc định **Junior 1 · 4 giờ**.
 *
 * Đây là **định nghĩa** để mọi người chọn điểm cùng một cỡ, không phải hệ số
 * tính toán: giờ/điểm của velocity, hiệu suất đội và Auto Assign vẫn đo từ dữ
 * liệu (`PaceService`, `hoursPerPoint`) — một hằng số đi thẳng vào hạn chót là
 * con số không ai biết nó không đến từ dữ liệu. Story point vẫn là khối lượng
 * việc (2026-09-25); neo chỉ nói một điểm to cỡ nào.
 *
 * Chỗ dùng: popup ước lượng đưa **việc mốc** lên trước (`ReferenceAnchorMatch`).
 */
export const STORY_POINT_ANCHOR_HOURS_MIN = 0.5;
export const STORY_POINT_ANCHOR_HOURS_MAX = 40;
export const StoryPointAnchor = z.object({
    level: StaffLevel,
    /** Bội của 0,5 giờ — cùng độ mịn với ô ước lượng giờ. */
    hours: z
        .number()
        .min(STORY_POINT_ANCHOR_HOURS_MIN)
        .max(STORY_POINT_ANCHOR_HOURS_MAX)
        .refine((value) => Number.isInteger(value * 2), { message: 'Số giờ phải là bội của 0,5' }),
});
export const DEFAULT_STORY_POINT_ANCHOR = { level: 'JUNIOR_1', hours: 4 };
export const UpdateStoryPointAnchorInput = StoryPointAnchor;
/**
 * "Làm khoảng `hours` giờ" = giờ thật trong `hours × (1 ± 25%)`. Đủ rộng để một
 * việc 3,5 giờ vẫn là mốc của neo 4 giờ, đủ hẹp để việc 8 giờ thì không.
 */
export const ANCHOR_HOURS_TOLERANCE = 0.25;
export function anchorHoursRange(anchor) {
    return {
        min: anchor.hours * (1 - ANCHOR_HOURS_TOLERANCE),
        max: anchor.hours * (1 + ANCHOR_HOURS_TOLERANCE),
    };
}
/**
 * Vì sao một việc mẫu là **việc mốc** — người dùng chốt 2026-10-22:
 *
 * 1. Việc **của chính người làm** có 1 SP (`own` + `onePoint`).
 * 2. Không có thì việc **cùng loại** của người khác có 1 SP (`onePoint`), hoặc làm
 *    xong trong khoảng giờ ≈1 SP theo bậc người làm (`levelHours`,
 *    `oneStoryPointHoursRange` — 2026-11-02: Senior làm 1 SP ít giờ hơn Junior 1).
 *
 * Người làm chưa xếp bậc thì không bao giờ `levelHours` — không đoán bậc.
 */
export const ReferenceAnchorMatch = z.object({
    own: z.boolean(),
    onePoint: z.boolean(),
    levelHours: z.boolean(),
});
/** Tối đa bấy nhiêu việc mốc trong popup — chừa chỗ cho việc giống nhất. */
export const MAX_ANCHOR_CANDIDATES = 2;
/**
 * Gộp việc mốc lên trước danh sách việc mẫu thường, bỏ trùng, cắt ở `max`.
 * Hàm thuần — API gọi, test được từng ca.
 */
export function withAnchorsFirst(anchors, others, max) {
    const picked = anchors.slice(0, MAX_ANCHOR_CANDIDATES);
    const seen = new Set(picked.map((item) => item.taskId));
    return [...picked, ...others.filter((item) => !seen.has(item.taskId))].slice(0, max);
}
/**
 * Hệ số tốc độ mặc định theo bậc — người dùng chốt bảng này 2026-09-30 (nút "Điền
 * tự động theo bậc", docs/plan-project-rate-speed.md §11). Chuyển từ web sang đây ở
 * APM-SPA-e để popup ước lượng dùng cùng bảng.
 *
 * `1,00` = người ở mức chuẩn (middle, §3); `> 1` chậm hơn, `< 1` nhanh hơn — cùng
 * chiều với `velocity_sample.ratio`. Chênh lệch giữa các bậc **lớn dần theo độ
 * khó**: ở việc vặt ai cũng làm gần như nhau, ở việc khó thì kinh nghiệm mới tạo
 * khác biệt. Senior ở việc dễ không nhanh hơn bao nhiêu — "senior hay sa đà ở việc
 * vặt" (§3.2).
 */
export const LEVEL_SPEED_DEFAULTS = {
    JUNIOR_1: { simple: 1.2, standard: 1.4, hard: 1.8 },
    JUNIOR_2: { simple: 1.05, standard: 1.15, hard: 1.35 },
    SENIOR_1: { simple: 0.95, standard: 0.9, hard: 0.85 },
    SENIOR_2: { simple: 0.95, standard: 0.8, hard: 0.7 },
};
/**
 * Quy giờ thật của một việc **chưa có SP** ra SP theo neo — APM-SPA-e, người dùng
 * yêu cầu 2026-11-02.
 *
 * Giờ của người làm → giờ chuẩn (`÷` hệ số bậc của họ) → giờ của bậc neo (`×` hệ số
 * bậc neo) → `÷` giờ neo. Ví dụ neo Junior 1 · 4 giờ, việc 9 giờ ở mức "chuẩn":
 * người chưa xếp bậc ⇒ 9 × 1,4 ÷ 4 ≈ 3,2 SP; Senior 2 làm ⇒ 9 ÷ 0,8 × 1,4 ÷ 4 ≈ 3,9 SP.
 *
 * Người làm **chưa xếp bậc** coi như mức chuẩn (1,00) — chính là nghĩa của giờ ước
 * lượng (glossary "giờ của một người middle chuẩn"). `bucket` là mức khó suy từ giờ
 * (`bucketOf` của `@apm/estimate`); `null` coi như `standard`.
 *
 * Chỉ để **hiện** (gợi ý trên màn hình): không ghi vào việc mẫu, không ghi vào việc
 * đang ước lượng — SP thật vẫn chỉ do người chọn. Làm tròn 0,1, tối thiểu 0,1.
 */
export function convertedStoryPoints(input) {
    if (!(input.hours > 0) || !(input.anchor.hours > 0))
        return null;
    const bucket = input.bucket ?? 'standard';
    const doer = input.doerLevel ? LEVEL_SPEED_DEFAULTS[input.doerLevel][bucket] : 1;
    const anchorHours = (input.hours / doer) * LEVEL_SPEED_DEFAULTS[input.anchor.level][bucket];
    return Math.max(0.1, Math.round((anchorHours / input.anchor.hours) * 10) / 10);
}
/** SP của việc đang ước lượng từ SP quy đổi của mẫu: `× (1 + tỷ lệ)`, làm tròn 0,1. */
export function scaleConvertedPoints(points, ratioPct) {
    return Math.max(0.1, Math.round(points * (1 + ratioPct / 100) * 10) / 10);
}
/**
 * Số giờ để một người bậc `level` làm xong **1 SP** — người dùng nhắc 2026-11-02: "không
 * phải ai cũng làm 4h là 1 sp vì 1 sp tính bằng 4h của level junior 1, với senior thì 1
 * sp phải làm ngắn hơn". Nghịch đảo của `convertedStoryPoints`: giờ neo ÷ hệ số bậc neo
 * × hệ số bậc này. Neo Junior 1 · 4 giờ (mức "dễ"): Junior 1 = 4 giờ, Senior 2 ≈ 3,2
 * giờ, chưa xếp bậc (mức chuẩn) ≈ 3,3 giờ. `bucket` là mức khó của giờ neo
 * (`bucketOf(anchor.hours)` ở `@apm/estimate`); `null` coi như `standard`.
 */
export function oneStoryPointHours(input) {
    const bucket = input.bucket ?? 'standard';
    const own = input.level ? LEVEL_SPEED_DEFAULTS[input.level][bucket] : 1;
    return (input.anchor.hours / LEVEL_SPEED_DEFAULTS[input.anchor.level][bucket]) * own;
}
/** "Khoảng 1 SP" của bậc ấy: `oneStoryPointHours × (1 ± 25%)` — cùng biên với `anchorHoursRange`. */
export function oneStoryPointHoursRange(input) {
    return anchorHoursRange({ hours: oneStoryPointHours(input) });
}
/**
 * Giờ cho **1 SP** của một người theo từng mức khó — thứ server đưa cho giao diện thay
 * vì tên bậc (người dùng chốt 2026-11-02: tên bậc không hiện cho mọi thành viên, chỉ PM /
 * quản trị). Giao diện nhân ra giờ bằng `hoursFromRates`; ra cùng số với
 * `hoursForStoryPoints` của server.
 */
export const StoryPointRates = z.object({
    simple: z.number().positive(),
    standard: z.number().positive(),
    hard: z.number().positive(),
});
export function storyPointRatesOf(level, anchor) {
    return {
        simple: oneStoryPointHours({ level, bucket: 'simple', anchor }),
        standard: oneStoryPointHours({ level, bucket: 'standard', anchor }),
        hard: oneStoryPointHours({ level, bucket: 'hard', anchor }),
    };
}
/**
 * Giờ cho `points` SP từ bảng giờ/SP: `points × rates[bucket]`, làm tròn 0,5 giờ, tối
 * thiểu 0,5. `bucket` là mức khó của `points × giờ neo` (`bucketOf` ở `@apm/estimate`) —
 * việc to thì chênh giữa các bậc lớn hơn; `null` coi như `standard`.
 */
export function hoursFromRates(points, rates, bucket) {
    const hours = points * rates[bucket ?? 'standard'];
    return Math.max(0.5, Math.round(hours * 2) / 2);
}
/**
 * Giờ ước lượng cho `points` SP khi người làm ở bậc `level` — người dùng yêu cầu
 * 2026-11-02: "số sp điền tay, là bao nhiêu thì số giờ sẽ tự động đổi theo theo tốc độ
 * tương ứng của level hiện tại được giao task". Người chưa xếp bậc = mức chuẩn.
 */
export function hoursForStoryPoints(input) {
    return hoursFromRates(input.points, storyPointRatesOf(input.level, input.anchor), input.bucket);
}
/**
 * Giờ/SP của **một người** — `level` chỉ có khi người xem quản lý được giá thuê và tốc độ
 * của dự án (`project_rate:manage`); người khác chỉ thấy con số (2026-11-02).
 */
export const MemberStoryPointRates = z.object({
    rates: StoryPointRates,
    level: StaffLevel.nullable().optional(),
});
/** `GET /projects/:id/story-point-rates` — form việc tự tính giờ từ SP theo người được giao. */
export const ProjectStoryPointRates = z.object({
    anchor: StoryPointAnchor,
    members: z.array(MemberStoryPointRates.extend({ userId: z.uuid() })),
});
//# sourceMappingURL=story-point-anchor.js.map